import React from 'react';
import { StyleSheet } from 'react-native';

import Placeholder, { EmptySearchPlaceholder } from '@components/Placeholders';
import ScreenContainer from '@components/ScreenContainer';
import SearchBar from '@components/SearchBar';
import { LargeUserCardList } from '@components/User/CardList';
import { useUserListDeletion } from '@components/User/useUserListDeletion';
import { LoadingView } from '@components/View';
import { useCurrentUser } from '@context/UsersContext';
import { RequestMutationAction, useJoinGroupRequestMutation } from '@db/Requests/Queries';
import { useGroupMembersQuery } from '@db/Users/Queries';
import { useLocalSearch } from '@helpers/LocalSearch';
import { createAppNavigation } from '@navigation/AppNavigation';
import { ViewGroupMembersScreenProps } from '@navigation/Types';
import { User } from '@types';

const USER_SEARCH_KEYS = ['displayName', 'username', 'hometown.displayName'];

export default function ViewGroupMembersScreen({ route, navigation }: ViewGroupMembersScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const { userDoc } = useCurrentUser();
  const groupId = route.params.groupId;
  const [searchText, setSearchText] = React.useState<string>('');
  const isAdmin = Boolean(userDoc?.adminGroupIds?.includes(groupId));
  const membersQuery = useGroupMembersQuery({ groupId });
  const joinGroupMutation = useJoinGroupRequestMutation();
  const memberDeletion = useUserListDeletion<User>({
    scopeKey: groupId,
    users: membersQuery.members,
    onDelete: (user, handlers) => {
      joinGroupMutation.mutate(
        { action: RequestMutationAction.DELETE, groupId, userId: user.userId },
        handlers
      );
    },
  });
  const { loading: searchLoading, results: searchedMembers } = useLocalSearch<User>({
    items: memberDeletion.visibleUsers,
    keys: USER_SEARCH_KEYS,
    query: searchText,
  });

  const renderHeader = () => (
    <SearchBar
      value={searchText}
      onChangeText={(text) => setSearchText(text)}
      loading={searchLoading}
      placeholder="Search members"
      style={styles.searchBar}
      testID="screens.ViewGroupMembersScreen.SearchBar"
    />
  );

  const renderEmpty = () => (
    <LoadingView loading={searchLoading} indicatorSize="large">
      {searchText ? (
        <EmptySearchPlaceholder />
      ) : (
        <Placeholder
          title="No members"
          icon="account-multiple"
          message="Group members will appear here"
        />
      )}
    </LoadingView>
  );

  return (
    <ScreenContainer
      loading={membersQuery.loading}
      error={membersQuery.error}
      errorProps={membersQuery.errorProps}
      style={styles.background}>
      <LargeUserCardList
        data={searchedMembers}
        itemProps={{
          onPress: (user) => appNavigation.openProfile(user.userId),
          messageButton: {
            onPress: appNavigation.openChat,
          },
        }}
        deletable={() => isAdmin}
        deleting={memberDeletion.isDeleting}
        onDelete={memberDeletion.deleteUser}
        ListHeaderComponent={renderHeader()}
        ListEmptyComponent={renderEmpty()}
        style={styles.list}
        testID="screens.ViewGroupMembersScreen.FlatList.Members"
      />
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  background: {
    alignItems: 'stretch',
  },
  list: {
    flex: 1,
    width: '100%',
  },
  searchBar: {
    margin: 5,
    marginTop: 10,
  },
});
